import { useEffect, useRef } from 'react';
import { XIcon } from 'lucide-react';
import { Input } from '@rocco/components/input';
import type { ContactSuggestion } from '../../types';
import { ParticipantAvatar } from '../avatar/ParticipantAvatar';

/**
 * A search input to search for contact suggestions and a list of recipients
 * to show when creating a new conversation.
 * @see {@link ChatBox}
 */
export function ChatBoxRecipients({
    search,
    recipients,
    maxRecipients,
    isOpen,
    onSearchChange,
    onRecipientsChange,
    onInputRef,
}: {
    search: string;
    recipients: ContactSuggestion[];
    maxRecipients: number;
    isOpen: boolean;
    onSearchChange: (search: string) => void;
    onRecipientsChange: (recipients: ContactSuggestion[]) => void;
    onInputRef?: (element: HTMLInputElement | undefined) => void;
}) {
    const inputRef = useRef<HTMLInputElement>();

    useEffect(() => {
        // Focus the input when the chat box is opened.
        if (isOpen) {
            inputRef.current?.focus();
        }
    }, [isOpen]);

    return (
        <div className="space-y-[8px]">
            {recipients.length > 0 && (
                <div className="flex flex-wrap gap-[4px]">
                    {recipients.map((recipient) => (
                        <div
                            key={recipient.ID}
                            className="flex items-center gap-[8px] overflow-hidden rounded-[8px] bg-white/30 p-[4px]"
                        >
                            <ParticipantAvatar
                                className="size-[28px]"
                                fallbackClassName="!text-[13px]"
                                participant={recipient}
                            />
                            <div className="min-w-0 flex-1 truncate text-[12px] font-medium leading-tight">
                                {recipient.Name}
                            </div>
                            <button
                                className="self-start p-[2px]"
                                onClick={() => {
                                    onRecipientsChange(
                                        recipients.filter(
                                            (r) => r.ID !== recipient.ID
                                        )
                                    );
                                    inputRef.current?.focus();
                                }}
                            >
                                <XIcon
                                    className="size-[12px] text-gray-600"
                                    strokeWidth={2.5}
                                />
                            </button>
                        </div>
                    ))}
                </div>
            )}
            <Input
                ref={(element) => {
                    inputRef.current = element ?? undefined;
                    onInputRef?.(element ?? undefined);
                }}
                value={search}
                className="text-[16px] md:!text-[14px]"
                color="contrast"
                placeholder="Type a name to search profiles..."
                autoComplete="off"
                showClearButton
                autoFocus
                onChange={(e) => onSearchChange(e.target.value)}
            />
            {recipients.length >= maxRecipients && !!search && (
                <div className="px-[4px] py-[2px] text-[12px] text-red-600">
                    You can only add up to {maxRecipients} recipients
                </div>
            )}
        </div>
    );
}
